import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiRequest } from "../../api/client";
import {
  ConfirmModal,
  EditorModal,
  ErrorText
} from "../catalog/CatalogPages";

type RuleScope = "SCHOOL" | "GRADES" | "CLASSES";
type RuleKind = "NON_ADJACENT" | "ADJACENT";
type AdjacentOrder = "A_BEFORE_B" | "B_BEFORE_A" | "ANY";

interface RuleReference {
  id: string;
  label: string;
  active: boolean;
}

interface RuleClassReference extends RuleReference {
  grade_code: number;
}

interface CourseTimeRule {
  id: string;
  course_a: RuleReference;
  course_b: RuleReference;
  scope: RuleScope;
  grade_codes: number[];
  classes: RuleClassReference[];
  affected_class_count: number;
}

interface CourseAdjacentRule extends CourseTimeRule {
  order: AdjacentOrder;
}

interface CourseTimeRulesResponse {
  condition_revision: number;
  locked: boolean;
  courses: RuleReference[];
  classes: RuleClassReference[];
  grades: Array<{ grade_code: number; label: string }>;
  rules: CourseTimeRule[];
  adjacent_rules: CourseAdjacentRule[];
}

interface RuleForm {
  courseAId: string;
  courseBId: string;
  scope: RuleScope;
  gradeCodes: number[];
  classIds: string[];
  order: AdjacentOrder;
}

const emptyForm: RuleForm = {
  courseAId: "",
  courseBId: "",
  scope: "SCHOOL",
  gradeCodes: [],
  classIds: [],
  order: "ANY"
};

export function CourseTimeRulesPanel({
  projectId,
  conditionRevision,
  classCoursePairs
}: {
  projectId: string;
  conditionRevision: number;
  classCoursePairs: Array<{ classId: string; courseId: string }>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{
    kind: RuleKind;
    item: CourseTimeRule | CourseAdjacentRule | "new";
  } | null>(null);
  const [deleting, setDeleting] = useState<{
    kind: RuleKind;
    item: CourseTimeRule | CourseAdjacentRule;
  } | null>(null);
  const [form, setForm] = useState<RuleForm>(emptyForm);
  const rules = useQuery({
    queryKey: ["course-time-rules", projectId],
    queryFn: () =>
      apiRequest<CourseTimeRulesResponse>(
        `/projects/${projectId}/course-time-rules`
      )
  });
  const mutation = useMutation({
    mutationFn: (action: "save" | "delete") => {
      if (!rules.data) throw new Error("课程不相邻设置尚未加载完成");
      if (action === "delete" && deleting) {
        const base = deleting.kind === "ADJACENT"
          ? "course-adjacent-rules"
          : "course-time-rules";
        return apiRequest(
          `/projects/${projectId}/${base}/${deleting.item.id}?expected_condition_revision=${conditionRevision}`,
          { method: "DELETE" }
        );
      }
      if (!editing) throw new Error("请先选择要保存的设置");
      const base = editing.kind === "ADJACENT"
        ? "course-adjacent-rules"
        : "course-time-rules";
      const path = editing.item === "new"
        ? `/projects/${projectId}/${base}`
        : `/projects/${projectId}/${base}/${editing.item.id}`;
      return apiRequest(path, {
        method: editing.item === "new" ? "POST" : "PATCH",
        body: JSON.stringify({
          course_a_id: form.courseAId,
          course_b_id: form.courseBId,
          scope: form.scope,
          grade_codes: form.scope === "GRADES" ? form.gradeCodes : [],
          class_ids: form.scope === "CLASSES" ? form.classIds : [],
          ...(editing.kind === "ADJACENT" ? { order: form.order } : {}),
          expected_condition_revision: conditionRevision
        })
      });
    },
    onSuccess: async () => {
      setEditing(null);
      setDeleting(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["course-time-rules", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["planning-draft", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["plans", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["planning", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      ]);
    }
  });

  function openNew(kind: RuleKind): void {
    mutation.reset();
    setForm(emptyForm);
    setEditing({ kind, item: "new" });
  }

  function openEdit(kind: RuleKind, rule: CourseTimeRule | CourseAdjacentRule): void {
    mutation.reset();
    setForm({
      courseAId: rule.course_a.id,
      courseBId: rule.course_b.id,
      scope: rule.scope,
      gradeCodes: rule.grade_codes,
      classIds: rule.classes.map((item) => item.id),
      order: "order" in rule ? rule.order : "ANY"
    });
    setEditing({ kind, item: rule });
  }

  const selectedClassIds = new Set(
    form.scope === "SCHOOL"
      ? rules.data?.classes.map((item) => item.id) ?? []
      : form.scope === "GRADES"
        ? rules.data?.classes
            .filter((item) => form.gradeCodes.includes(item.grade_code))
            .map((item) => item.id) ?? []
        : form.classIds
  );
  const pairs = new Set(
    classCoursePairs.map((item) => `${item.classId}:${item.courseId}`)
  );
  const affectedCount = [...selectedClassIds].filter(
    (classId) =>
      pairs.has(`${classId}:${form.courseAId}`) &&
      pairs.has(`${classId}:${form.courseBId}`)
  ).length;
  const formInvalid =
    !form.courseAId ||
    !form.courseBId ||
    form.courseAId === form.courseBId ||
    (form.scope === "GRADES" && form.gradeCodes.length === 0) ||
    (form.scope === "CLASSES" && form.classIds.length === 0);

  return (
    <section className="content-card course-time-rules" aria-label="课程相邻关系设置">
      <div className="section-heading compact-heading">
        <h2>课程相邻关系设置</h2>
      </div>
      {rules.isLoading ? <p className="state-message">正在加载设置…</p> : null}
      {rules.isError ? <ErrorText error={rules.error} /> : null}
      {rules.data ? (
        <div className="course-relation-columns">
          <section aria-label="课程不相邻设置">
            <div className="course-relation-heading">
              <div><h3>课程不相邻设置</h3><span>自动排课时两门课不紧挨。</span></div>
              <button className="ghost-button" type="button" disabled={rules.data.locked || rules.data.courses.length < 2} onClick={() => openNew("NON_ADJACENT")}>添加设置</button>
            </div>
            {rules.data.rules.length === 0 ? <p className="state-message">暂无设置。</p> : (
              <div className="course-time-rule-list">
                {rules.data.rules.map((rule) => (
                  <article key={rule.id}>
                    <p>{rule.course_a.label}与{rule.course_b.label}不相邻，应用范围是{scopeLabel(rule, rules.data.grades)}。</p>
                    <div className="row-actions">
                      <button className="link-button" type="button" disabled={rules.data.locked} onClick={() => openEdit("NON_ADJACENT", rule)}>编辑</button>
                      <button className="link-button danger-text" type="button" disabled={rules.data.locked} onClick={() => setDeleting({ kind: "NON_ADJACENT", item: rule })}>删除</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
          <section aria-label="课程相邻设置">
            <div className="course-relation-heading">
              <div><h3>课程相邻设置</h3><span>按课时组严格成对，多出的课时组单独排。</span></div>
              <button className="ghost-button" type="button" disabled={rules.data.locked || rules.data.courses.length < 2} onClick={() => openNew("ADJACENT")}>添加设置</button>
            </div>
            {rules.data.adjacent_rules.length === 0 ? <p className="state-message">暂无设置。</p> : (
              <div className="course-time-rule-list">
                {rules.data.adjacent_rules.map((rule) => (
                  <article key={rule.id}>
                    <p>{adjacentRuleLabel(rule)}，应用范围是{scopeLabel(rule, rules.data.grades)}。</p>
                    <div className="row-actions">
                      <button className="link-button" type="button" disabled={rules.data.locked} onClick={() => openEdit("ADJACENT", rule)}>编辑</button>
                      <button className="link-button danger-text" type="button" disabled={rules.data.locked} onClick={() => setDeleting({ kind: "ADJACENT", item: rule })}>删除</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      ) : null}
      {editing && rules.data ? (
        <EditorModal
          wide
          title={`${editing.item === "new" ? "添加" : "编辑"}${editing.kind === "ADJACENT" ? "课程相邻" : "课程不相邻"}设置`}
          onClose={() => setEditing(null)}
          onSubmit={() => mutation.mutate("save")}
          pending={mutation.isPending}
          submitDisabled={formInvalid}
          error={mutation.error}
        >
          <div className="plan-form-grid">
            <label className="field">
              <span>课程 1</span>
              <select value={form.courseAId} onChange={(event) => setForm({ ...form, courseAId: event.target.value })}>
                <option value="">请选择</option>
                {rules.data.courses.map((course) => <option key={course.id} value={course.id}>{course.label}</option>)}
              </select>
            </label>
            <label className="field">
              <span>课程 2</span>
              <select value={form.courseBId} onChange={(event) => setForm({ ...form, courseBId: event.target.value })}>
                <option value="">请选择</option>
                {rules.data.courses.map((course) => <option key={course.id} value={course.id} disabled={course.id === form.courseAId}>{course.label}</option>)}
              </select>
            </label>
            <label className="field plan-form-wide">
              <span>适用范围</span>
              <select value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value as RuleScope, gradeCodes: [], classIds: [] })}>
                <option value="SCHOOL">全校</option>
                <option value="GRADES">指定年级</option>
                <option value="CLASSES">指定班级</option>
              </select>
            </label>
            {editing.kind === "ADJACENT" ? (
              <label className="field plan-form-wide">
                <span>先后顺序</span>
                <select value={form.order} onChange={(event) => setForm({ ...form, order: event.target.value as AdjacentOrder })}>
                  <option value="ANY">不限先后</option>
                  <option value="A_BEFORE_B">课程 1 在前</option>
                  <option value="B_BEFORE_A">课程 2 在前</option>
                </select>
              </label>
            ) : null}
          </div>
          {form.scope === "GRADES" ? (
            <ChoiceGrid
              label="选择年级"
              options={rules.data.grades.map((item) => ({ id: String(item.grade_code), label: item.label }))}
              value={form.gradeCodes.map(String)}
              onChange={(value) => setForm({ ...form, gradeCodes: value.map(Number) })}
            />
          ) : null}
          {form.scope === "CLASSES" ? (
            <ChoiceGrid
              label="选择班级"
              options={rules.data.classes.map((item) => ({ id: item.id, label: item.label }))}
              value={form.classIds}
              onChange={(classIds) => setForm({ ...form, classIds })}
            />
          ) : null}
          <p className="impact-summary">当前选择影响 {affectedCount} 个同时开设这两门课的班级。</p>
          {editing.kind === "ADJACENT" ? (
            <p className="warning-note" role="note">连课会作为一个整体参与相邻，例如可形成 AAB；两门都是连课时可形成 AABB。相邻规则会先作为硬目标排课，可能与可排节次、教师或其他条件冲突。若首轮无法排出，系统只会提示是否放宽，不会自动忽略。</p>
          ) : null}
        </EditorModal>
      ) : null}
      {deleting ? (
        <ConfirmModal
          title={`删除课程${deleting.kind === "ADJACENT" ? "相邻" : "不相邻"}设置`}
          description={`删除“${deleting.item.course_a.label}与${deleting.item.course_b.label}${deleting.kind === "ADJACENT" ? "相邻" : "不相邻"}”后，下次自动排课不再应用这条关系。`}
          dangerLabel="确认删除"
          onClose={() => setDeleting(null)}
          onConfirm={() => mutation.mutate("delete")}
          pending={mutation.isPending}
          error={mutation.error}
        />
      ) : null}
    </section>
  );
}

function ChoiceGrid({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: Array<{ id: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
}): React.JSX.Element {
  return (
    <fieldset className="rule-choice-grid">
      <legend>{label}</legend>
      <div>
        {options.map((option) => (
          <label key={option.id}>
            <input
              type="checkbox"
              checked={value.includes(option.id)}
              onChange={(event) => onChange(event.target.checked ? [...value, option.id] : value.filter((id) => id !== option.id))}
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function scopeLabel(
  rule: CourseTimeRule,
  grades: Array<{ grade_code: number; label: string }>
): string {
  if (rule.scope === "SCHOOL") return "全校";
  if (rule.scope === "CLASSES") return rule.classes.map((item) => item.label).join("、");
  const labels = new Map(grades.map((item) => [item.grade_code, item.label]));
  return rule.grade_codes.map((code) => labels.get(code) ?? `${code}年级`).join("、");
}

function adjacentRuleLabel(rule: CourseAdjacentRule): string {
  if (rule.order === "A_BEFORE_B") {
    return `${rule.course_a.label}后紧接${rule.course_b.label}`;
  }
  if (rule.order === "B_BEFORE_A") {
    return `${rule.course_b.label}后紧接${rule.course_a.label}`;
  }
  return `${rule.course_a.label}与${rule.course_b.label}相邻（不限先后）`;
}
